import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { productApi, Product, CategoryNode } from '../../services/api'
import { formatPrice } from '../../utils/currency'
import ProductImageCarousel from '../product/ProductImageCarousel'
import { useTelegramSafeAreas } from '../layout/PageLayout'
import { useCartContext } from '../../contexts/CartContext'
import { Linkify } from '../ui/Linkify'
import { ReviewForm, ReviewItem, StarRating } from '../ui/ReviewComponents'
import { reviewApi, Review } from '../../services/api'
import { Heart, MessageCircle, User, AlertCircle, Check, MapPin, CreditCard, Tag, Clock, ChevronLeft } from 'lucide-react'
import { t } from '../../i18n/translations'

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  const currentUser = tg?.initDataUnsafe?.user
  const safeAreas = useTelegramSafeAreas()
  const { addToCart, isItemInCart, getItemQuantity, removeItem, cart } = useCartContext()

  const inCart = id ? isItemInCart(id) : false
  const quantity = id ? getItemQuantity(id) : 0

  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [showInterestModal, setShowInterestModal] = useState(false)
  const [interestMessage, setInterestMessage] = useState('')
  const [addingToCart, setAddingToCart] = useState(false)
  const [proposedPrice, setProposedPrice] = useState('')
  const [shareIdentity, setShareIdentity] = useState(true)
  const [isSendingInterest, setIsSendingInterest] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [catAttrs, setCatAttrs] = useState<NonNullable<CategoryNode['attributes']>>([])
  const [sellerReviews, setSellerReviews] = useState<Review[]>([])
  const [sellerRating, setSellerRating] = useState<{ avg: number; count: number }>({ avg: 0, count: 0 })
  const [showReviewForm, setShowReviewForm] = useState(false)

  // Load the category's attribute schema so spec values render with proper labels.
  useEffect(() => {
    if (!product?.category_id) { setCatAttrs([]); return }
    let cancelled = false
    productApi.getCategoryTree('en').then(({ categories }) => {
      const find = (nodes: CategoryNode[]): CategoryNode | undefined => {
        for (const n of nodes) {
          if (n.id === product.category_id) return n
          const hit = n.children && find(n.children)
          if (hit) return hit
        }
        return undefined
      }
      const node = find(categories || [])
      if (!cancelled) setCatAttrs(node?.attributes || [])
    }).catch(() => { if (!cancelled) setCatAttrs([]) })
    return () => { cancelled = true }
  }, [product?.category_id])

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError('Product ID is missing')
        setLoading(false)
        return
      }

      try {
        logger.debug('ProductDetail: Fetching product', id)
        const data = await productApi.getProduct(id)
        logger.debug('ProductDetail: Product fetched', data)
        setProduct(data)
      } catch (error) {
        logger.error('ProductDetail: Error fetching product:', error)
        setError(error instanceof Error ? error.message : 'Failed to load product')
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [id])

  // Fetch seller reviews when product owner is known
  useEffect(() => {
    if (!product?.owner?.telegram_id) return
    const fetchReviews = async () => {
      try {
        // Need to find user UUID from telegram_id - use ratings endpoint
        const ownerTgId = product.owner!.telegram_id.toString()
        // For now use the owner's telegram_id as user identifier in reviews
        const [reviewsData, ratingsData] = await Promise.all([
          reviewApi.getUserReviews(ownerTgId, { type: 'seller', limit: 5 }),
          reviewApi.getUserRatings(ownerTgId),
        ])
        setSellerReviews(reviewsData.reviews || [])
        setSellerRating({
          avg: ratingsData.avg_seller_rating,
          count: ratingsData.seller_review_count,
        })
      } catch {
        // Reviews not critical - fail silently
      }
    }
    fetchReviews()
  }, [product?.owner?.telegram_id])

  const handleSendInterest = async () => {
    if (!id) return

    setIsSendingInterest(true)
    setError(null)

    try {
      logger.debug('ProductDetail: Sending interest', { product_id: id, message: interestMessage })

      // Check if user is authenticated by checking for auth token
      const authToken = localStorage.getItem('auth_token')
      const hasValidAuth = authToken && authToken.length > 20 && !authToken.startsWith('telegram:')

      if (hasValidAuth) {
        // Use authenticated endpoint if we have a valid token
        logger.debug('ProductDetail: Using authenticated interest endpoint')
        const price = proposedPrice ? parseFloat(proposedPrice) : undefined
        await productApi.sendProductInterest(id, interestMessage || undefined, price)
      } else {
        // Use public endpoint for unauthenticated visitors
        logger.debug('ProductDetail: Using public interest endpoint', { shareIdentity })

        // Build visitor data based on shareIdentity toggle
        const visitorData: any = {}

        // Only include visitor info if shareIdentity is true
        if (shareIdentity && tg?.initDataUnsafe?.user) {
          const tgUser = tg.initDataUnsafe.user
          visitorData.telegram_id = tgUser.id
          visitorData.telegram_user = tgUser.username
          visitorData.visitor_name = tgUser.first_name || tgUser.username || undefined
          logger.debug('ProductDetail: Including visitor identity', { telegram_id: tgUser.id })
        } else {
          logger.debug('ProductDetail: Visitor chose to remain anonymous')
        }

        if (interestMessage) {
          visitorData.message = interestMessage
        }

        if (proposedPrice) {
          visitorData.proposed_price = parseFloat(proposedPrice)
        }

        await productApi.sendPublicProductInterest(id, visitorData)
      }

      logger.info('ProductDetail: Interest sent successfully')

      // Success feedback
      if (tg?.HapticFeedback) {
        try {
          tg.HapticFeedback.notificationOccurred('success')
        } catch (e) {
          // Ignore haptic feedback errors
          logger.debug('Haptic feedback not available')
        }
      }

      // Show success message in UI instead of using tg.showAlert
      setSuccessMessage('Your interest has been sent to the seller!')
      setError(null)

      // Close modal and reset after a delay
      setTimeout(() => {
        setShowInterestModal(false)
        setInterestMessage('')
        setProposedPrice('')
        setShareIdentity(true)
        setSuccessMessage(null)
      }, 2000)
    } catch (error) {
      logger.error('ProductDetail: Failed to send interest:', error)
      setError(error instanceof Error ? error.message : 'Failed to send interest')
      setSuccessMessage(null)

      if (tg?.HapticFeedback) {
        try {
          tg.HapticFeedback.notificationOccurred('error')
        } catch (e) {
          // Ignore haptic feedback errors
          logger.debug('Haptic feedback not available')
        }
      }
    } finally {
      setIsSendingInterest(false)
    }
  }

  const handleAddToCart = async () => {
    if (!id) return

    try {
      setAddingToCart(true)

      if (inCart) {
        // Toggle OFF: remove from favorites
        const cartItem = cart?.items?.find(i => i.product_id === id)
        if (cartItem) {
          await removeItem(cartItem.id)
        }
        if (tg?.HapticFeedback) {
          tg.HapticFeedback.notificationOccurred('warning')
        }
      } else {
        // Toggle ON: add to favorites
        await addToCart(id, 1)
        if (tg?.HapticFeedback) {
          tg.HapticFeedback.notificationOccurred('success')
        }
      }
    } catch (error) {
      logger.error('ProductDetail: Failed to add to cart:', error)
      if (tg?.showAlert) {
        tg.showAlert('Failed to add to cart')
      }
    } finally {
      setAddingToCart(false)
    }
  }

  // Check if current user is the owner
  const isOwner = currentUser && product?.owner?.telegram_id === currentUser.id

  // Always-visible in-app back button (Telegram's native BackButton is not shown
  // reliably on the product page, e.g. for sold items).
  const backButton = (
    <button
      onClick={() => navigate(-1)}
      aria-label="Back"
      className="fixed left-3 z-50 w-9 h-9 rounded-full bg-black/45 backdrop-blur-md
        text-white flex items-center justify-center active:scale-90 transition-transform shadow-lg"
      style={{ top: `calc(${safeAreas.top}px + 8px)` }}
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  )

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {backButton}
        <div style={{ paddingTop: `${safeAreas.top}px` }} />
        <main className="flex-1 overflow-auto">
          <div className="max-w-md mx-auto">
            {/* Image skeleton */}
            <div className="w-full aspect-square bg-gray-200 animate-pulse" />
            {/* Content skeleton */}
            <div className="p-4 space-y-3">
              <div className="h-6 bg-gray-200 rounded-lg w-3/4 animate-pulse" />
              <div className="h-8 bg-gray-200 rounded-lg w-1/3 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded-lg w-full animate-pulse" />
              <div className="h-4 bg-gray-200 rounded-lg w-2/3 animate-pulse" />
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {backButton}
        {/* Top padding for Telegram UI */}
        <div style={{ paddingTop: `${safeAreas.top}px` }}></div>

        <main className="flex-1 overflow-auto p-4">
          <div className="max-w-md mx-auto text-center py-8">
            <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-8">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">Product Not Found</h2>
              <p className="text-gray-500 mb-6">{error || 'Could not load product details'}</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold text-sm
                  hover:bg-blue-600 active:scale-[0.98] transition-all
                  shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
              >
                Browse Products
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {backButton}
      {/* Top padding for Telegram UI */}
      <div style={{ paddingTop: `${safeAreas.top}px` }} />

      {/* Scrollable content */}
      <main className="flex-1 overflow-auto" style={{ paddingBottom: '80px' }}>
        {/* Full-width image gallery */}
        <div className="w-full">
          <ProductImageCarousel
            images={product.images}
            productName={product.name}
            fallbackImage={product.image_url || undefined}
          />
        </div>

        {/* Video Gallery */}
        {product.videos && product.videos.length > 0 && (
          <div className="px-4 space-y-3 mt-4">
            {product.videos.map((video) => (
              <video
                key={video.id}
                src={video.video_url}
                poster={video.thumbnail_url || undefined}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-2xl bg-black"
              />
            ))}
          </div>
        )}

        {/* Product Info — padded to max-w-md */}
        <div className="max-w-md mx-auto px-4 space-y-4 mt-4">
          <div className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
            <h1 className="text-xl font-bold text-gray-800 mb-2">{product.name}</h1>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-2xl font-bold text-blue-600">
                {formatPrice(product.price, product.currency)}
              </span>
              {product.compare_price && product.compare_price > product.price && (
                <span className="text-base text-gray-400 line-through">
                  {formatPrice(product.compare_price, product.currency)}
                </span>
              )}
            </div>

            {/* Sold / unavailable banner */}
            {product.status !== 'active' && (
              <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm font-medium text-amber-800">
                  This item is no longer available — it may have been sold.
                </span>
              </div>
            )}

            {/* Status Badge & Condition */}
            <div className="mb-4 flex flex-wrap gap-2">
              {product.status === 'active' && (
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                  <Check className="w-3.5 h-3.5" />
                  Available
                </span>
              )}
              {product.condition && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                  <Tag className="w-3.5 h-3.5" />
                  {t(`product.conditions.${product.condition}`)}
                </span>
              )}
              {product.quantity !== undefined && (
                <span className="text-sm text-gray-600 self-center ml-2">
                  {product.quantity} in stock
                </span>
              )}
            </div>

            {/* Specifications */}
            {product.attributes && Object.keys(product.attributes).length > 0 && (
              <div className="mb-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {t('product.specifications')}
                </p>
                <div className="space-y-1.5">
                  {(catAttrs.length
                    ? catAttrs
                    : Object.keys(product.attributes).map((k) => ({ key: k, type: 'text', label: { en: k } } as any))
                  )
                    .filter((a: any) => {
                      const v = product.attributes![a.key]
                      return v !== undefined && v !== null && v !== ''
                    })
                    .map((a: any) => {
                      const raw = product.attributes![a.key]
                      const label = typeof a.label === 'object' ? (a.label.en || a.key) : (a.label || a.key)
                      let display: string
                      if (a.type === 'bool') display = raw ? t('common.yes') : t('common.no')
                      else if (a.type === 'enum' && a.options) {
                        const opt = a.options.find((o: any) => o.v === String(raw))
                        display = opt ? (opt.en || opt.label || opt.v) : String(raw)
                      } else display = `${raw}${a.unit ? ' ' + a.unit : ''}`
                      return (
                        <div key={a.key} className="flex justify-between text-sm">
                          <span className="text-gray-500">{label}</span>
                          <span className="text-gray-800 font-medium">{display}</span>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            {/* Location */}
            {product.city_name && (
              <div className="mb-4 flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>
                  {product.city_name}
                  {product.country_code && `, ${product.country_code}`}
                </span>
              </div>
            )}

            {/* Payment Methods */}
            {product.payment_methods && product.payment_methods.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="font-medium text-sm">{t('product.paymentMethods')}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.payment_methods.map((method: string) => (
                    <span
                      key={method}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                    >
                      {t(`product.payments.${method}`)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
                <Linkify text={product.description} className="text-gray-600 whitespace-pre-wrap" />
              </div>
            )}

            {/* Owner Info */}
            {product.owner && (
              <div className="pt-4 mt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">
                      {product.owner.first_name || product.owner.username || 'Anonymous'}
                    </p>
                    {product.owner.username && (
                      <p className="text-sm text-gray-500">@{product.owner.username}</p>
                    )}
                  </div>
                  {sellerRating.count > 0 && (
                    <StarRating rating={sellerRating.avg} count={sellerRating.count} size="md" />
                  )}
                </div>

                {/* View full seller profile */}
                <button
                  onClick={() => navigate(`/seller/${product.owner!.telegram_id}`)}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                    border border-gray-200 text-blue-600 font-medium text-sm
                    hover:bg-gray-50 active:scale-[0.98] transition-all"
                >
                  <User className="w-4 h-4" />
                  <span>{t('profile.viewSellerProfile')}</span>
                </button>

                {/* Seller Reviews */}
                {sellerRating.count > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">
                        {t('reviews.title')} ({sellerRating.count})
                      </h4>
                      {!isOwner && (
                        <button
                          onClick={() => setShowReviewForm(!showReviewForm)}
                          className="text-xs text-blue-500 font-medium"
                        >
                          {showReviewForm ? t('reviews.cancel') : t('reviews.writeReview')}
                        </button>
                      )}
                    </div>
                    {sellerReviews.slice(0, 3).map((r) => (
                      <ReviewItem key={r.id} review={r} />
                    ))}
                    {showReviewForm && product.owner && (
                      <div className="mt-3">
                        <ReviewForm
                          revieweeId={product.owner.telegram_id.toString()}
                          reviewType="seller"
                          onSuccess={(review) => {
                            setSellerReviews((prev) => [review, ...prev])
                            setSellerRating((prev) => ({
                              avg: ((prev.avg * prev.count + review.rating) / (prev.count + 1)),
                              count: prev.count + 1,
                            }))
                            setShowReviewForm(false)
                          }}
                          onCancel={() => setShowReviewForm(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
                {sellerRating.count === 0 && !isOwner && (
                  <button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="mt-3 text-sm text-blue-500 font-medium"
                  >
                    {showReviewForm ? t('reviews.cancel') : t('reviews.beFirst')}
                  </button>
                )}
                {showReviewForm && sellerRating.count === 0 && product.owner && (
                  <div className="mt-3">
                    <ReviewForm
                      revieweeId={product.owner.telegram_id.toString()}
                      reviewType="seller"
                      onSuccess={(review) => {
                        setSellerReviews([review])
                        setSellerRating({ avg: review.rating, count: 1 })
                        setShowReviewForm(false)
                      }}
                      onCancel={() => setShowReviewForm(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reservation Status Banner */}
          {product.is_reserved && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-700">
                <Clock className="w-5 h-5" />
                <span className="font-medium">{t('product.reservation.reserved')}</span>
              </div>
              {product.reservation_expires_at && (
                <p className="text-sm text-orange-600 mt-1">
                  Expires: {new Date(product.reservation_expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {isOwner && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-blue-700 text-sm">This is your product - you can test the notification system</p>
            </div>
          )}
        </div>
      </main>

      {/* Sticky Bottom Action Bar */}
      {product.status === 'active' && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md
            border-t border-gray-100 px-4 py-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 12px)' }}
        >
          <div className="max-w-md mx-auto flex gap-3">
            {/* Favorite Button */}
            {inCart ? (
              <button
                onClick={handleAddToCart}
                disabled={addingToCart}
                className="flex-1 bg-green-50 text-green-700 py-3 rounded-xl font-semibold text-sm
                  border border-green-200 hover:bg-green-100 transition-colors
                  disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <Heart className="w-4 h-4 fill-green-600 text-green-600" />
                <span>Saved ({quantity})</span>
              </button>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={addingToCart}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold text-sm
                  hover:bg-gray-200 transition-colors
                  disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <Heart className="w-4 h-4" />
                <span>{addingToCart ? 'Saving...' : 'Save'}</span>
              </button>
            )}

            {/* Contact / Reserve */}
            {product.is_reserved ? (
              <button
                disabled
                className="flex-1 bg-gray-200 text-gray-500 py-3 rounded-xl font-semibold text-sm
                  cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                <span>{t('product.reservation.reserved')}</span>
              </button>
            ) : (
              <button
                onClick={() => setShowInterestModal(true)}
                className="flex-1 bg-blue-500 text-white py-3 rounded-xl font-semibold text-sm
                  hover:bg-blue-600 transition-colors flex items-center justify-center gap-2
                  active:scale-[0.98] shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
              >
                <MessageCircle className="w-4 h-4" />
                <span>I'm Interested</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Interest Modal */}
      {showInterestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-[60]"
          onClick={(e) => { if (e.target === e.currentTarget && !isSendingInterest) {
            setShowInterestModal(false); setInterestMessage(''); setProposedPrice('');
            setShareIdentity(true); setError(null); setSuccessMessage(null)
          }}}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl animate-slide-up">
            {successMessage ? (
              /* Success state */
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Interest Sent! 🎉</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  The seller will receive your message via Telegram.
                  {currentUser?.first_name && (
                    <> They'll see <strong>{currentUser.first_name}</strong> and can contact you directly.</>
                  )}
                </p>
                <button
                  onClick={() => { setShowInterestModal(false); setSuccessMessage(null) }}
                  className="mt-6 bg-blue-500 text-white px-8 py-2.5 rounded-xl font-semibold text-sm
                    hover:bg-blue-600 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              /* Form state */
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Send Interest</h2>
                <p className="text-gray-500 text-sm mb-4">
                  The seller will be notified via Telegram.
                </p>

                {/* Identity Sharing Toggle */}
                <div className="mb-4 bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">Share my contact</label>
                    <button
                      onClick={() => setShareIdentity(!shareIdentity)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        shareIdentity ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        shareIdentity ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {shareIdentity
                      ? `Seller will see ${currentUser?.first_name || 'your name'}${currentUser?.username ? ` (@${currentUser.username})` : ''}`
                      : 'You will appear as anonymous visitor'}
                  </p>
                </div>

                {/* Proposed Price */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Proposed Price <span className="text-gray-400 font-normal">— Optional</span>
                  </label>
                  <input
                    type="number"
                    value={proposedPrice}
                    onChange={(e) => setProposedPrice(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800
                      focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                    placeholder={`Your offer (listed: ${formatPrice(product.price, product.currency)})`}
                    min="0" step="0.01"
                  />
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Message <span className="text-gray-400 font-normal">— Optional</span>
                  </label>
                  <textarea
                    value={interestMessage}
                    onChange={(e) => setInterestMessage(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800
                      focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm resize-none"
                    placeholder="Hi! Is this still available? I'm interested..."
                    rows={3}
                  />
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 rounded-xl p-3 text-sm text-red-600">{error}</div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowInterestModal(false); setInterestMessage(''); setProposedPrice('')
                      setShareIdentity(true); setError(null); setSuccessMessage(null)
                    }}
                    disabled={isSendingInterest}
                    className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm
                      hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInterest}
                    disabled={isSendingInterest}
                    className="flex-1 bg-blue-500 text-white py-3 rounded-xl font-semibold text-sm
                      hover:bg-blue-600 transition-colors disabled:opacity-50
                      shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
                  >
                    {isSendingInterest ? 'Sending...' : 'Send Interest'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
